import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Box,
  Button,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';

import { formatDashboardPercentage } from '../../../../utils/statisticsDashboard';

import CommonDashboardCard from '../commonDashboardCard';
import DonutCardModal from '../donutCardModal';
import DonutChart from '../donutChart';
import DonutLegendItem from '../donutLegendItem';

const COMPACT_ITEM_LIMIT = 5;

export default function DonutCard({
  title,
  data = [],
  total,
  centerValue,
}) {
  const intl = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const hasData = data.some(item => Number(item?.value) > 0);
  const visibleData = hasData ? data.slice(0, COMPACT_ITEM_LIMIT) : [];
  const hiddenData = hasData ? data.slice(COMPACT_ITEM_LIMIT) : [];
  const hiddenCount = hiddenData.length;
  const dataTotal = data.reduce(
    (sum, item) => sum + (Number(item?.value) || 0),
    0
  );
  const hiddenValue = hiddenData.reduce(
    (sum, item) => sum + (Number(item?.value) || 0),
    0
  );
  const compactChartData = hiddenCount > 0
    ? [
      ...visibleData,
      {
        key: 'others',
        name: intl.formatMessage({
          id: 'dashboard.new.operation.others',
        }),
        value: hiddenValue,
        percentage: dataTotal > 0
          ? formatDashboardPercentage(hiddenValue * 100 / dataTotal)
          : 0,
        color: '#94a3b8',
      },
    ]
    : visibleData;

  const renderEmptyState = () => (
    <Typography
      variant="body2"
      sx={{
        color: '#64748b',
        fontSize: '.8rem',
        textAlign: 'center',
      }}
    >
      {intl.formatMessage({
        id: 'dashboard.new.operation.emptyPeriod',
      })}
    </Typography>
  );

  return (
    <>
      <CommonDashboardCard
        hasInfo
        cardHeaderProps={{ title }}
      >
        <CardContent
          sx={{
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
              alignItems: 'center',
              justifyItems: 'center',
              gap: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
              <DonutChart
                data={compactChartData}
                total={total}
                showCenterValue={centerValue}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  width: '100%',
                  maxWidth: 320,
                }}
              >
                {hasData
                  ? visibleData.map((item, index) => (
                    <DonutLegendItem
                      key={item.key || `${item.name}-${index}`}
                      item={item}
                    />
                  ))
                  : renderEmptyState()}

                {hiddenCount > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setModalOpen(true)}
                    sx={{
                      alignSelf: 'flex-start',
                      px: 0,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1
                    }}
                  >
                    {intl.formatMessage(
                      { id: 'dashboard.new.operation.viewOthers' },
                      { count: hiddenCount }
                    )}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </CommonDashboardCard>

      <DonutCardModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={title}
        data={data}
        total={total}
        centerValue={centerValue}
      />
    </>
  );
}
